import React, { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import Box from '@amzn/meridian/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Tag from '@amzn/meridian/tag';
import { FormHandler } from 'src/hooks/useForm';
import { createSearchRegExp } from 'src/utils/searchUtils';
import { useAppSelector } from 'src/store/store';
import {
  setupRegionTaskMap,
  TaskGroupTableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';

// **** Types **** //

export type FormDataProps = Pick<TaskGroupTableDataRow, 'label' | 'region' | 'tasks'>;

interface TaskGroupModalFormProps {
  form: FormHandler<FormDataProps>;
}

// **** Components **** //

/**
 * Default component
 */
const TaskGroupModalForm = (props: TaskGroupModalFormProps) => {
  const { form } = props;
  const { regions, taskTableData, taskGroupTableData, selectedTaskGroupTableRow } = useAppSelector(
    (state) => state.taskConfigPage,
  );
  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [taskSearchQuery, setTaskSearchQuery] = useState('');

  // Track which task belong to a region
  const regionTaskMap = useMemo(() => setupRegionTaskMap(taskGroupTableData), [taskGroupTableData]);

  // When the region changes, reset the selected tasks and task options. We need to
  // filter out tasks already belonging to this region, unless we are editing
  // and that task already belongs to the current taskGroup-region.
  const regionFilteredTaskOptions = useMemo(() => {
    if (form.formData.region) {
      const set = regionTaskMap.get(form.formData.region);
      const isEditing = !!selectedTaskGroupTableRow;
      return taskTableData.filter((task) => {
        if (!task.regions.includes(form.formData.region)) {
          return false;
        }
        const notTaken = !set?.has(task.id);
        if (isEditing) {
          return notTaken || selectedTaskGroupTableRow.tasks.some((t) => task.id === t.id);
        } else {
          return notTaken;
        }
      });
    } else {
      return taskTableData;
    }
  }, [form.formData.region, taskTableData, regionTaskMap]);

  // Search regions
  const matchedRegionOptions = useMemo(() => {
    const searchRegExp = createSearchRegExp(regionSearchQuery);
    return regions
      .map((region) => ({ label: region.name, value: region.name }))
      .filter((option) => !regionSearchQuery || searchRegExp.test(option.label));
  }, [regions, regionSearchQuery]);

  // Search Tasks
  const matchedTaskOptions = useMemo(() => {
    const searchRegExp = createSearchRegExp(taskSearchQuery);
    return regionFilteredTaskOptions
      .filter((t) => !taskSearchQuery || searchRegExp.test(t.label))
      .map((t) => ({ label: t.label, value: t.id }));
  }, [taskSearchQuery, regionFilteredTaskOptions]);

  // Set the selected-tasks from an array of ids
  const setTasks = useCallback(
    (taskIds: number[]) => {
      const tasks = regionFilteredTaskOptions.filter((t) => taskIds.includes(t.id));
      form.handleChange({ tasks });
    },
    [regionFilteredTaskOptions],
  );

  // Remove a selected-task
  const dismissTask = useCallback(
    (taskId: number) => {
      const tasks = form.formData.tasks.filter((t) => t.id != taskId);
      form.handleChange({ tasks });
    },
    [form.formData.tasks],
  );

  return (
    <Box className="view-and-edit-tasks-modal-form" spacingInset="100 100 300 100">
      {/* Name */}
      <Box spacingInset="none none 200 none">
        <Text>Group Name*</Text>
      </Box>
      <Input
        type="text"
        placeholder="Enter group name..."
        value={form.formData.label}
        onChange={(label: string) => form.handleChange({ label })}
        error={form.errors.label?.error}
        errorMessage={form.errors.label?.errorMessage}
      />
      <Box spacingInset="300" />

      {/* Region */}
      <Text>Region/State/Province*</Text>
      <Select
        searchQuery={regionSearchQuery}
        onSearch={(v) => setRegionSearchQuery(v)}
        placeholder="Select region..."
        value={form.formData.region}
        // Reset "tasks" when the region updates
        onChange={(region: string) => form.updatePartial({ region, tasks: [] })}
        error={form.errors.region?.error}
        errorMessage={form.errors.region?.errorMessage}
      >
        {matchedRegionOptions.map((o) => (
          <SelectOption label={o.label} value={o.value} key={o.value} />
        ))}
      </Select>
      <Box spacingInset="300" />

      {/* Child Tasks */}
      <Text>Add Tasks to Group</Text>
      <Select
        searchQuery={taskSearchQuery}
        onSearch={(v) => setTaskSearchQuery(v)}
        placeholder={
          form.formData.region
            ? !!matchedTaskOptions.length
              ? 'Select...'
              : 'No Tasks avaialble for this region'
            : 'Must select region first'
        }
        value={form.formData.tasks.map((t) => t.id)}
        onChange={setTasks}
        error={form.errors.tasks?.error}
        errorMessage={form.errors.tasks?.errorMessage}
        disabled={!form.formData.region || !matchedTaskOptions.length}
      >
        {matchedTaskOptions.map((o) => (
          <SelectOption label={o.label} value={o.value} key={o.value} />
        ))}
      </Select>
      <Box spacingInset="300" />

      {/* Display selected child tasks */}
      <Box className="tags-container">
        {form.formData.tasks.map((task) => (
          <Tag key={task.id} type="neutral" onClose={() => dismissTask(task.id)}>
            {task.label}
          </Tag>
        ))}
      </Box>
    </Box>
  );
};

// **** Export **** //

export default TaskGroupModalForm;
